import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from '@reduxjs/toolkit';
import type { RootState } from '../index';

// Study Group types
export interface StudyGroupMember {
  id: string;
  userId: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
  skillLevel: number; // 0-1
  masteryAreas: string[];
  joinedAt: string;
  lastActive: string;
  role: 'creator' | 'moderator' | 'member';
  contributionScore: number;
  engagementLevel: 'high' | 'medium' | 'low';
  isOnline: boolean;
}

export interface StudyGroup {
  id: string;
  name: string;
  description?: string;
  creatorId: string;
  journeyFocus: string[];
  skillLevel: string;
  skillLevelRange: [number, number];
  maxMembers: number;
  currentMembers: number;
  members: StudyGroupMember[];
  status: 'active' | 'inactive' | 'completed' | 'archived';
  privacy: 'public' | 'private' | 'invite_only';
  inviteCode?: string;
  tags: string[];
  createdAt: string;
  lastActivity: string;
  isFull: boolean;
  isActive: boolean;
  averageEngagement: number;
  completionRate: number;
  groupDynamics: GroupDynamics;
}

export interface GroupDynamics {
  cohesion: number; // 0-1
  collaboration: number; // 0-1
  diversity: number; // 0-1
  activityLevel: number; // 0-1
  supportiveness: number; // 0-1
  learningEffectiveness: number; // 0-1
}

export interface StudyGroupRecommendation {
  group: StudyGroup;
  relevanceScore: number;
  compatibilityScore: number;
  aiReasoning: string;
  confidence: number;
  matchingFactors: string[];
  commonInterests: string[];
  skillAlignment: number;
  scheduleCompatibility: number;
  learningStyleMatch: number;
}

export interface CollaborativeLearningActivity {
  id: string;
  title: string;
  description: string;
  type: 'discussion' | 'practice_session' | 'challenge' | 'review' | 'project';
  difficulty: number; // 0-1
  estimatedDuration: number; // minutes
  requiredSkills: string[];
  optimalGroupSize: [number, number];
  materials: string[];
  objectives: string[];
  aiGenerated: boolean;
  adaptedForGroup: boolean;
  groupSkillAlignment: number;
  engagementPrediction: number;
}

export interface GroupChallenge {
  id: string;
  title: string;
  description: string;
  type:
    | 'brewing_challenge'
    | 'knowledge_quiz'
    | 'skill_practice'
    | 'creative_project';
  difficulty: number;
  balancedForGroup: boolean;
  duration: number; // minutes
  maxParticipants: number;
  currentParticipants: number;
  skillRequirements: string[];
  rewards: ChallengeReward[];
  progress: GroupChallengeProgress;
  createdAt: string;
  startsAt: string;
  endsAt: string;
  status: 'upcoming' | 'active' | 'completed' | 'cancelled';
  aiOptimized: boolean;
}

export interface ChallengeReward {
  type: 'points' | 'badge' | 'achievement' | 'unlock';
  value: number;
  description: string;
  individual: boolean;
  collective: boolean;
}

export interface GroupChallengeProgress {
  overallProgress: number; // 0-1
  memberProgress: Record<string, number>;
  milestones: ChallengeMilestone[];
  currentPhase: string;
  timeRemaining: number;
  difficultyAdjustments: DifficultyAdjustment[];
}

export interface ChallengeMilestone {
  id: string;
  title: string;
  description: string;
  targetProgress: number;
  achieved: boolean;
  achievedAt?: string;
  reward?: ChallengeReward;
}

export interface DifficultyAdjustment {
  timestamp: string;
  reason: string;
  adjustment: number; // -1 to 1
  aiReasoning: string;
  memberFeedback: string[];
}

export interface GroupProgressInsight {
  groupId: string;
  overallProgress: number;
  memberProgressDistribution: Record<string, number>;
  skillGrowthAreas: string[];
  collaborationEffectiveness: number;
  learningVelocity: number;
  engagementTrends: EngagementTrend[];
  recommendations: ProgressRecommendation[];
  collectiveAchievements: Achievement[];
  nextMilestones: Milestone[];
}

export interface EngagementTrend {
  period: string;
  engagement: number;
  participation: number;
  collaboration: number;
  satisfaction: number;
}

export interface ProgressRecommendation {
  type: 'activity' | 'focus_area' | 'group_dynamic' | 'schedule';
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  aiReasoning: string;
  expectedImpact: number;
  implementationEffort: number;
}

export interface Achievement {
  id: string;
  title: string;
  description: string;
  type: 'individual' | 'collective';
  earnedAt: string;
  members: string[];
  icon: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

export interface Milestone {
  id: string;
  title: string;
  description: string;
  targetDate: string;
  progress: number;
  requirements: string[];
  reward?: ChallengeReward;
}

export interface StudyGroupAnalytics {
  groupId: string;
  totalMembers: number;
  activeMembers: number;
  averageEngagement: number;
  sessionFrequency: number;
  completionRate: number;
  satisfactionScore: number;
  learningEffectiveness: number;
  retentionRate: number;
  growthRate: number;
  collaborationIndex: number;
  diversityIndex: number;
  activityMetrics: ActivityMetrics;
  memberMetrics: MemberMetrics;
  contentMetrics: ContentMetrics;
  timeMetrics: TimeMetrics;
  lastUpdated: string;
}

export interface ActivityMetrics {
  totalActivities: number;
  completedActivities: number;
  averageParticipation: number;
  mostPopularActivityTypes: string[];
  activitySuccessRate: number;
  averageActivityDuration: number;
}

export interface MemberMetrics {
  averageSkillLevel: number;
  skillLevelDistribution: Record<string, number>;
  engagementDistribution: Record<string, number>;
  contributionScores: Record<string, number>;
  memberSatisfaction: Record<string, number>;
  memberRetention: number;
}

export interface ContentMetrics {
  contentConsumption: number;
  contentCreation: number;
  contentSharing: number;
  contentRating: number;
  mostEngagingContent: string[];
  contentCompletionRate: number;
}

export interface TimeMetrics {
  averageSessionDuration: number;
  peakActivityHours: string[];
  sessionFrequency: number;
  timeToCompletion: number;
  scheduleAdherence: number;
}

interface StudyGroupState {
  recommendations: StudyGroupRecommendation[];
  myGroups: StudyGroup[];
  currentGroup: StudyGroup | null;
  activities: CollaborativeLearningActivity[];
  challenges: GroupChallenge[];
  progressInsights: Record<string, GroupProgressInsight>;
  analytics: Record<string, StudyGroupAnalytics>;
  loading: {
    recommendations: boolean;
    groups: boolean;
    activities: boolean;
    challenges: boolean;
    analytics: boolean;
    insights: boolean;
  };
  error: string | null;
  lastUpdated: string | null;
  optimizationInProgress: boolean;
}

const initialState: StudyGroupState = {
  recommendations: [],
  myGroups: [],
  currentGroup: null,
  activities: [],
  challenges: [],
  progressInsights: {},
  analytics: {},
  loading: {
    recommendations: false,
    groups: false,
    activities: false,
    challenges: false,
    analytics: false,
    insights: false,
  },
  error: null,
  lastUpdated: null,
  optimizationInProgress: false,
};

// Async thunks
export const fetchStudyGroupRecommendations = createAsyncThunk(
  'studyGroup/fetchRecommendations',
  async (userId: string, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual GraphQL query
      const response = await fetch(
        `/api/ai/study-groups/recommendations/${userId}`
      );
      if (!response.ok) {
        throw new Error(
          `Failed to fetch recommendations: ${response.statusText}`
        );
      }
      const recommendations: StudyGroupRecommendation[] = await response.json();
      return recommendations;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch study group recommendations'
      );
    }
  }
);

export const generateOptimalGroupFormation = createAsyncThunk(
  'studyGroup/generateOptimalFormation',
  async (
    params: {
      members: string[];
      learningGoals: string[];
      skillLevels: Record<string, number>;
      preferences: Record<string, any>;
    },
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual AI optimization service
      const response = await fetch('/api/ai/study-groups/optimize-formation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      if (!response.ok) {
        throw new Error(
          `Failed to optimize group formation: ${response.statusText}`
        );
      }
      const optimizedGroups: StudyGroup[] = await response.json();
      return optimizedGroups;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to generate optimal group formation'
      );
    }
  }
);

export const generateCollaborativeLearningActivities = createAsyncThunk(
  'studyGroup/generateActivities',
  async (
    params: {
      groupId: string;
      memberSkills: Record<string, string[]>;
      learningGoals: string[];
      timeConstraints: number;
    },
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual AI activity generation service
      const response = await fetch('/api/ai/study-groups/generate-activities', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      if (!response.ok) {
        throw new Error(
          `Failed to generate activities: ${response.statusText}`
        );
      }
      const activities: CollaborativeLearningActivity[] = await response.json();
      return activities;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to generate collaborative learning activities'
      );
    }
  }
);

export const createBalancedGroupChallenge = createAsyncThunk(
  'studyGroup/createBalancedChallenge',
  async (
    params: {
      groupId: string;
      challengeType: GroupChallenge['type'];
      memberSkills: Record<string, number>;
      duration: number;
      objectives: string[];
    },
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual AI challenge balancing service
      const response = await fetch(
        '/api/ai/study-groups/create-balanced-challenge',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params),
        }
      );
      if (!response.ok) {
        throw new Error(
          `Failed to create balanced challenge: ${response.statusText}`
        );
      }
      const challenge: GroupChallenge = await response.json();
      return challenge;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to create balanced group challenge'
      );
    }
  }
);

export const fetchGroupProgressInsights = createAsyncThunk(
  'studyGroup/fetchProgressInsights',
  async (groupId: string, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual GraphQL query
      const response = await fetch(
        `/api/ai/study-groups/progress-insights/${groupId}`
      );
      if (!response.ok) {
        throw new Error(
          `Failed to fetch progress insights: ${response.statusText}`
        );
      }
      const insights: GroupProgressInsight = await response.json();
      return { groupId, insights };
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch group progress insights'
      );
    }
  }
);

export const fetchStudyGroupAnalytics = createAsyncThunk(
  'studyGroup/fetchAnalytics',
  async (groupId: string, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual GraphQL query
      const response = await fetch(`/api/ai/study-groups/analytics/${groupId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch analytics: ${response.statusText}`);
      }
      const analytics: StudyGroupAnalytics = await response.json();
      return { groupId, analytics };
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch study group analytics'
      );
    }
  }
);

const studyGroupSlice = createSlice({
  name: 'studyGroup',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    setCurrentGroup: (state, action: PayloadAction<StudyGroup | null>) => {
      state.currentGroup = action.payload;
    },
    updateGroupMember: (
      state,
      action: PayloadAction<{
        groupId: string;
        memberId: string;
        updates: Partial<StudyGroupMember>;
      }>
    ) => {
      const group = state.myGroups.find(g => g.id === action.payload.groupId);
      if (group) {
        const member = group.members.find(
          m => m.id === action.payload.memberId
        );
        if (member) {
          Object.assign(member, action.payload.updates);
        }
      }
    },
    updateChallengeProgress: (
      state,
      action: PayloadAction<{
        challengeId: string;
        progress: Partial<GroupChallengeProgress>;
      }>
    ) => {
      const challenge = state.challenges.find(
        c => c.id === action.payload.challengeId
      );
      if (challenge) {
        challenge.progress = {
          ...challenge.progress,
          ...action.payload.progress,
        };
      }
    },
    addActivityToGroup: (
      state,
      action: PayloadAction<CollaborativeLearningActivity>
    ) => {
      state.activities.push(action.payload);
    },
    setOptimizationInProgress: (state, action: PayloadAction<boolean>) => {
      state.optimizationInProgress = action.payload;
    },
  },
  extraReducers: builder => {
    // Fetch Study Group Recommendations
    builder
      .addCase(fetchStudyGroupRecommendations.pending, state => {
        state.loading.recommendations = true;
        state.error = null;
      })
      .addCase(fetchStudyGroupRecommendations.fulfilled, (state, action) => {
        state.loading.recommendations = false;
        state.recommendations = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchStudyGroupRecommendations.rejected, (state, action) => {
        state.loading.recommendations = false;
        state.error = action.payload as string;
      });

    // Generate Optimal Group Formation
    builder
      .addCase(generateOptimalGroupFormation.pending, state => {
        state.optimizationInProgress = true;
        state.error = null;
      })
      .addCase(generateOptimalGroupFormation.fulfilled, (state, action) => {
        state.optimizationInProgress = false;
        // Update recommendations with optimized groups
        state.recommendations = action.payload.map(group => ({
          group,
          relevanceScore: 0.9,
          compatibilityScore: 0.95,
          aiReasoning:
            'AI-optimized group formation based on skill complementarity and learning goals',
          confidence: 0.9,
          matchingFactors: [
            'skill_balance',
            'goal_alignment',
            'schedule_compatibility',
          ],
          commonInterests: group.journeyFocus,
          skillAlignment: 0.9,
          scheduleCompatibility: 0.8,
          learningStyleMatch: 0.85,
        }));
      })
      .addCase(generateOptimalGroupFormation.rejected, (state, action) => {
        state.optimizationInProgress = false;
        state.error = action.payload as string;
      });

    // Generate Collaborative Learning Activities
    builder
      .addCase(generateCollaborativeLearningActivities.pending, state => {
        state.loading.activities = true;
        state.error = null;
      })
      .addCase(
        generateCollaborativeLearningActivities.fulfilled,
        (state, action) => {
          state.loading.activities = false;
          state.activities = action.payload;
        }
      )
      .addCase(
        generateCollaborativeLearningActivities.rejected,
        (state, action) => {
          state.loading.activities = false;
          state.error = action.payload as string;
        }
      );

    // Create Balanced Group Challenge
    builder
      .addCase(createBalancedGroupChallenge.pending, state => {
        state.loading.challenges = true;
        state.error = null;
      })
      .addCase(createBalancedGroupChallenge.fulfilled, (state, action) => {
        state.loading.challenges = false;
        state.challenges.push(action.payload);
      })
      .addCase(createBalancedGroupChallenge.rejected, (state, action) => {
        state.loading.challenges = false;
        state.error = action.payload as string;
      });

    // Fetch Group Progress Insights
    builder
      .addCase(fetchGroupProgressInsights.pending, state => {
        state.loading.insights = true;
        state.error = null;
      })
      .addCase(fetchGroupProgressInsights.fulfilled, (state, action) => {
        state.loading.insights = false;
        state.progressInsights[action.payload.groupId] =
          action.payload.insights;
      })
      .addCase(fetchGroupProgressInsights.rejected, (state, action) => {
        state.loading.insights = false;
        state.error = action.payload as string;
      });

    // Fetch Study Group Analytics
    builder
      .addCase(fetchStudyGroupAnalytics.pending, state => {
        state.loading.analytics = true;
        state.error = null;
      })
      .addCase(fetchStudyGroupAnalytics.fulfilled, (state, action) => {
        state.loading.analytics = false;
        state.analytics[action.payload.groupId] = action.payload.analytics;
      })
      .addCase(fetchStudyGroupAnalytics.rejected, (state, action) => {
        state.loading.analytics = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setCurrentGroup,
  updateGroupMember,
  updateChallengeProgress,
  addActivityToGroup,
  setOptimizationInProgress,
} = studyGroupSlice.actions;

// Selectors
export const selectStudyGroupRecommendations = (state: RootState) =>
  state.studyGroup.recommendations;
export const selectMyStudyGroups = (state: RootState) =>
  state.studyGroup.myGroups;
export const selectCurrentGroup = (state: RootState) =>
  state.studyGroup.currentGroup;
export const selectCollaborativeLearningActivities = (state: RootState) =>
  state.studyGroup.activities;
export const selectGroupChallenges = (state: RootState) =>
  state.studyGroup.challenges;
export const selectGroupProgressInsights = (state: RootState) =>
  state.studyGroup.progressInsights;
export const selectStudyGroupAnalytics = (state: RootState) =>
  state.studyGroup.analytics;
export const selectStudyGroupLoading = (state: RootState) =>
  state.studyGroup.loading;
export const selectStudyGroupError = (state: RootState) =>
  state.studyGroup.error;
export const selectOptimizationInProgress = (state: RootState) =>
  state.studyGroup.optimizationInProgress;

// Complex selectors
export const selectHighCompatibilityGroupRecommendations = createSelector(
  [selectStudyGroupRecommendations],
  recommendations =>
    recommendations.filter(rec => rec.compatibilityScore >= 0.8)
);

export const selectActiveGroupChallenges = createSelector(
  [selectGroupChallenges],
  challenges => challenges.filter(challenge => challenge.status === 'active')
);

export const selectGroupActivitiesByType = createSelector(
  [selectCollaborativeLearningActivities],
  activities => {
    const grouped: Record<string, CollaborativeLearningActivity[]> = {};
    activities.forEach(activity => {
      if (!grouped[activity.type]) {
        grouped[activity.type] = [];
      }
      grouped[activity.type].push(activity);
    });
    return grouped;
  }
);

export const selectGroupInsightsByEngagement = createSelector(
  [selectGroupProgressInsights],
  insights => {
    return Object.entries(insights)
      .map(([groupId, insight]) => ({ groupId, ...insight }))
      .sort(
        (a, b) => b.collaborationEffectiveness - a.collaborationEffectiveness
      );
  }
);

export default studyGroupSlice.reducer;
